import MyContainer from '@/components/MyContainer';
import { BulkReceiveModal } from '@/components/products/BulkReceiveModal';
import StockClosingCard from '@/components/stock/StockClosingCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, PackagePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { StockList } from './components/StockList';
import { useStockPage } from './hooks/useStockPage';

export default function StockPage() {
  const { t } = useTranslation(['stock', 'common']);
  const {
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    showBulkReceiveModal,
    setShowBulkReceiveModal,
    stocks,
    filteredStocks,
    stats,
    totalElements,
    isLastPage,
    isLoading,
    isValidating,
    error,
    isMobile,
    handleRecomputeAllStock,
    handleBulkReceiveStock,
    refetchStock,
    loadMore,
  } = useStockPage();

  // StockList expects totalItems in stats
  const normalizedStats = {
    lowStockItems: stats.lowStockItems,
    outOfStockItems: stats.outOfStockItems,
    totalItems: stats.totalVariants ?? stocks.length,
  };

  if (error) {
    return (
      <div className="space-y-4 p-3 md:space-y-6 md:p-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 md:py-12 px-4">
            <div className="text-center">
              <AlertTriangle className="h-10 w-10 md:h-12 md:w-12 text-red-600 mx-auto mb-3 md:mb-4" />
              <h3 className="text-base md:text-lg font-semibold text-red-600">
                {t('stock:errorLoading')}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mt-2">
                {error.message || t('stock:errorMessage')}
              </p>
              <Button className="mt-4 w-full sm:w-auto" onClick={() => window.location.reload()}>
                {t('common:buttons.retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <MyContainer
      enableBack={false}
      title={t('stock:title')}
      subtitle={t('stock:description')}
      actions={
        <Button
          size="sm"
          onClick={() => setShowBulkReceiveModal(true)}
          className="w-full sm:w-auto"
        >
          <PackagePlus className="mr-2 h-4 w-4" />
          <span>{t('stock:receiveStock')}</span>
        </Button>
      }
    >
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="grid gap-3 md:gap-4">
          <StockClosingCard onRunClosing={handleRecomputeAllStock} />
        </div>

        <StockList
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          stocks={stocks}
          filteredStocks={filteredStocks}
          stats={normalizedStats}
          totalElements={totalElements}
          isLoading={isLoading}
          isMobile={isMobile}
          onStockUpdate={refetchStock}
          onLoadMore={loadMore}
          isLastPage={isLastPage}
          isValidating={isValidating}
        />

        <BulkReceiveModal
          isOpen={showBulkReceiveModal}
          productId={null}
          onSubmit={handleBulkReceiveStock}
          onCancel={() => setShowBulkReceiveModal(false)}
        />
      </div>
    </MyContainer>
  );
}
